/**
 * StepSkills — add skills as chips + AI suggest from experience.
 */

import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Input } from '../../ui/input';
import { StructureButton } from '../StructureButton';
import type { CVData } from '../../../types';
import type { Dispatch, SetStateAction } from 'react';

interface StepSkillsProps {
  data: CVData;
  setData: Dispatch<SetStateAction<CVData>>;
  removeSkill: (index: number) => void;
}

export function StepSkills({ data, setData, removeSkill }: StepSkillsProps) {
  const [newSkill, setNewSkill] = useState('');

  const handleAdd = () => {
    const trimmed = newSkill.trim();
    if (!trimmed) return;
    if (data.skills.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setNewSkill('');
      return;
    }
    setData((prev) => ({ ...prev, skills: [...prev.skills, trimmed] }));
    setNewSkill('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSuggest = (res: { skills?: string[] }) => {
    if (!res.skills?.length) return;
    setData((prev) => {
      const existing = new Set(prev.skills.map((s) => s.toLowerCase()));
      const toAdd = res.skills!.filter((s) => !existing.has(s.toLowerCase()));
      return { ...prev, skills: [...prev.skills, ...toAdd] };
    });
  };

  // Build context for suggestion from experience
  const expContext = data.experience
    .filter((e) => e.position || e.description)
    .map((e) => ({ position: e.position, company: e.company, description: e.description }));

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Tes compétences</h1>
        <p className="text-sm text-white/60">
          Ajoute tes compétences clés une par une, ou laisse l'IA en suggérer
          depuis ton parcours.
        </p>
      </div>

      {/* Chips */}
      {data.skills.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {data.skills.map((skill, i) => (
            <span
              key={i}
              className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 text-sm text-white/80"
            >
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(i)}
                className="text-white/30 hover:text-white/60 transition-colors"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add skill input */}
      <div className="flex gap-2">
        <Input
          type="text"
          value={newSkill}
          onChange={(e) => setNewSkill(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="React, Python, Excel…"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 text-sm"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!newSkill.trim()}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white/80 hover:border-white/20 transition-all disabled:opacity-40 text-sm font-medium shrink-0"
        >
          <Plus className="size-4" />
          Ajouter
        </button>
      </div>

      {/* AI suggest */}
      <div className="pt-1">
        <StructureButton
          type="skills_suggest"
          text={data.experience.map((e) => `${e.position} ${e.description}`).join(' ') || 'parcours professionnel'}
          context={{ experience: expContext }}
          onResult={handleSuggest}
          label="Suggérer depuis mon parcours"
          disabled={data.experience.length === 0 && data.skills.length === 0}
        />
        <p className="text-xs text-white/30 mt-1.5">
          L'IA analyse tes expériences pour suggérer des compétences pertinentes.
        </p>
      </div>
    </div>
  );
}
